"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  useToast,
  Spinner,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Divider,
} from "@chakra-ui/react";
import { useAppTheme } from "../ThemeContext.tsx";
import Layout from "./Layout.tsx";
import type { Call } from "../services/callService.ts";
import { supabase } from "../supabase.ts";

interface PhoneNumber {
  id: number;
  user_id: string;
  phone_number: string;
  purchased_at: string;
  deleted_at: string | null;
  is_active: boolean;
}

interface BillingInfo {
  phoneNumbers: PhoneNumber[];
  phoneNumberCount: number;
  phoneNumberMonthlyCost: number;
  totalCallCost: number;
  currentMonthCallCost: number;
  totalCost: number;
  currentMonthTotalCost: number;
  callCount: number;
  currentMonthCallCount: number;
  recentCalls: Call[];
}

const Billing = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      setIsLoading(true);

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast({
          title: "Authentication error",
          description: "Please log in to view billing information",
          status: "error",
          duration: 3000,
        });
        setIsLoading(false);
        return;
      }

      const response = await fetch("/api/billing/info", {
        headers: {
          "user-id": user.id, // Include user ID in the headers
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Server error: ${response.status}`
        );
      }

      const data = await response.json();
      setBillingInfo(data);
    } catch (error: any) {
      console.error("Error loading billing data:", error);
      toast({
        title: "Error loading billing data",
        description: error.message || "Please try again later",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <Flex justify="center" align="center" h="100vh" w="100%">
          <Spinner size="xl" />
        </Flex>
      </Layout>
    );
  }

  if (!billingInfo) {
    return (
      <Layout>
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Heading mb={6}>No Billing Data Available</Heading>
          <Text>
            Unable to load billing information. Please try again later.
          </Text>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        ml={{ base: 0, md: "20px" }}
        p={4}
      >
        <Heading mb={6}>Billing</Heading>

        <Flex mt="2rem" gap={8} width="100%" maxWidth="100%" flexWrap="wrap">
          <Card bg={isDark ? "gray.700" : "white"} flex="1">
            <CardHeader>
              <Heading size="md">Current Month Usage:</Heading>
            </CardHeader>
            <CardBody>
              <Stat mt="-2rem">
                <StatLabel>
                  <Heading size="md">Total Cost:</Heading>
                </StatLabel>
                <StatNumber fontSize="2xl">
                  ${billingInfo.currentMonthTotalCost.toFixed(2)}
                </StatNumber>
                <StatHelpText>
                  Since{" "}
                  {new Date().toLocaleString("default", { month: "long" })} 1st
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={isDark ? "gray.700" : "white"} flex="1">
            <CardHeader>
              <Heading size="md">All Time Usage:</Heading>
            </CardHeader>
            <CardBody>
              <Stat mt="-2rem">
                <StatLabel>
                  <Heading size="md">Total Cost:</Heading>
                </StatLabel>
                <StatNumber fontSize="2xl">
                  ${billingInfo.totalCost.toFixed(2)}
                </StatNumber>
                <StatHelpText>Since Sign-up</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Flex>

        <Card bg={isDark ? "gray.700" : "white"} width="100%" mt={8}>
          <CardHeader>
            <Heading size="md">Billing Breakdown</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Item</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Quantity</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Rate</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Cost</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>Phone Numbers (Monthly)</Td>
                  <Td>{billingInfo.phoneNumberCount}</Td>
                  <Td>$2.00 per number</Td>
                  <Td>${billingInfo.phoneNumberMonthlyCost.toFixed(2)}</Td>
                </Tr>
                <Tr>
                  <Td>Call Usage (Current Month)</Td>
                  <Td>{billingInfo.currentMonthCallCount}</Td>
                  <Td>Varies</Td>
                  <Td>${billingInfo.currentMonthCallCost.toFixed(2)}</Td>
                </Tr>
              </Tbody>
            </Table>

            <Divider my={4} />

            <Text fontWeight="bold" textAlign="right" pr={4}>
              Total Monthly Cost: $
              {billingInfo.currentMonthTotalCost.toFixed(2)}
            </Text>
          </CardBody>
        </Card>

        <Card bg={isDark ? "gray.700" : "white"} width="100%" mt={8}>
          <CardHeader>
            <Heading size="md">Active Phone Numbers</Heading>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Phone Number</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Purchased</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Monthly Cost</Th>
                </Tr>
              </Thead>
              <Tbody>
                {billingInfo.phoneNumbers &&
                billingInfo.phoneNumbers.length > 0 ? (
                  billingInfo.phoneNumbers.map((phoneNumber) => (
                    <Tr key={phoneNumber.id}>
                      <Td>{phoneNumber.phone_number}</Td>
                      <Td>
                        {new Date(
                          phoneNumber.purchased_at
                        ).toLocaleDateString()}
                      </Td>
                      <Td>$2.00</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={3} textAlign="center">
                      No active phone numbers
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </Box>
    </Layout>
  );
};

export default Billing;
